import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchContent, type ContentMap } from '@/api/client';

interface ContentContextValue {
  content: ContentMap;
  loading: boolean;
  refresh: () => void;
}

const ContentContext = createContext<ContentContextValue>({
  content: {},
  loading: true,
  refresh: () => {},
});

export function ContentProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [content, setContent] = useState<ContentMap>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const lang = i18n.language || 'en';
    const map = await fetchContent(lang);
    setContent(map);
    setLoading(false);
  }, [i18n.language]);

  const refresh = useCallback(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ContentContext.Provider value={{ content, loading, refresh }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}

/** Get a content value from API, fallback to i18n translation. */
export function useContentText(key: string, t: (k: string) => string, tKey: string): string {
  const { content } = useContent();
  return content[key]?.trim() || t(tKey);
}